"use client";

import { MediaProgressBar } from "@ui/components/core/MediaProgressBar";
import { useCallback, useState } from "react";
import { BoxedAspectRatioVideo } from "../../core/BoxedAspectRatioVideo/BoxedAspectRatioVideo";
import CoverArt from "@ui/components/routes/music/CoverArt";
import { MusicColorScheme } from "@ui/components/routes/music/colorScheme";

export interface HeroTranslateAudioMediaPlayerProps {
  className?: string;
  src: string;
  mode: "audio" | "video";
  audioColorScheme?: MusicColorScheme;
}

export function HeroTranslateMediaPlayer({
  src,
  className = "",
  mode,
  audioColorScheme,
}: HeroTranslateAudioMediaPlayerProps) {
  const [mediaElement, setMediaElement] = useState<HTMLMediaElement | null>(null);
  const [isMuted, setIsMuted] = useState(true);

  const handleVideoRef = useCallback((ref: HTMLVideoElement | null) => {
    setMediaElement(ref);
  }, []);

  const handleMuteToggle = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  const handleMediaClick = useCallback(() => {
    if (!mediaElement) return;

    if (mediaElement.paused) {
      mediaElement.play();
    } else {
      mediaElement.pause();
    }
  }, [mediaElement]);

  if (mode === "audio") {
    return (
      <div className={"relative"}>
        {/* hidden audio element to drive playback */}
        <audio
          ref={node => setMediaElement(node)}
          src={src}
          autoPlay
          preload='auto'
          crossOrigin='anonymous'
          onEnded={() => {
            if (mediaElement) {
              mediaElement.currentTime = 0;
              mediaElement.play();
            }
          }}
        />
        <CoverArt
          boxShadow={false}
          audioElement={mediaElement as HTMLAudioElement | null}
          colorScheme={audioColorScheme || MusicColorScheme.red}
          onClick={() => {
            if (!mediaElement) return;
            if (mediaElement.paused) mediaElement.play();
            else mediaElement.pause();
          }}
          songId={src}
        />
        <div className='bg-white absolute bottom-24 left-0 right-0 h-24'>
          <MediaProgressBar mediaRef={{ current: mediaElement }} size='lg' inverse />
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <BoxedAspectRatioVideo
        src={src}
        isMuted={isMuted}
        onMuteToggle={handleMuteToggle}
        onVideoRef={handleVideoRef}
        onVideoClick={handleMediaClick}
        autoPlay={true}
      >
        {/* Progress Bar Overlay */}
        <div className='absolute bottom-32 left-0 right-0'>
          <MediaProgressBar mediaRef={{ current: mediaElement }} size='lg' inverse />
        </div>
      </BoxedAspectRatioVideo>
    </div>
  );
}
